/**
 * dialog.js
 */

import React from 'react';
import {
  Button,
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogContentText,
  DialogActions as MuiDialogActions,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Icon from 'components/icon';
import DropMenu from 'components/drop-menu';


const useStyles = makeStyles(theme => ({
  title: {
    '&.MuiTypography-root': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    '& .icon': {
      marginRight: theme.spacing.dialogTitleIconMargin,
    }
  },
  titleDropMenuContainer: {
    '& > .MuiButton-root': {
      fontSize: theme.typography.sizes[3],
      fontWeight: 'normal',
      textTransform: 'none',
      color: 'inherit',
      '& .MuiSvgIcon-root.help': {
        fontSize: theme.typography.sizes[4],
        marginRight: theme.spacing(0.5),
        marginLeft: theme.spacing(0.5),
      },
      '&:hover': {
        backgroundColor: theme.palette.primary.colors[2],
      }
    },
  },
  titleDropMenu: {
    '& .MuiMenuItem-root': {
      whiteSpace: 'normal'
    }
  },
  enhancedActions: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey.colors[9],
    borderTop: `1px solid ${theme.palette.grey.colors[8]}`,
    '&.MuiDialogActions-spacing > :not(:first-child), & .MuiDialogActions-spacing > :not(:first-child)': {
      marginLeft: theme.spacing(1),
      [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(2),
      }
    } ,   
  },  
}));


export const DialogTitleDropMenu = (props) => {
  const { className, ...rest } = props;
  const classes = useStyles();

  return (
    <span className={classes.titleDropMenuContainer}>
      <DropMenu className={`${className} ${classes.titleDropMenu}`} {...rest} />
    </span>
  );
};


export const DialogTitle = (props) => {
  const {
    id,
    className, 
    icon,
    title, 
    actions,
  } = props;
  const classes = useStyles();

  return (
    <MuiDialogTitle className={`${classes.title} ${className || ''}`} id={id || 'dialog-title'}>
      <div className='flex-hc'>
        {icon && <Icon className='icon' type={icon} />}
        {title}
      </div>
      <div className={`flex-hc ${classes.titleActions}`}>{actions}</div>
    </MuiDialogTitle>
  )
}


export const DialogContent = (props) => {
  const { children, className, contentTextClassName } = props;

  return (
    <MuiDialogContent className={className || ''}>
      <DialogContentText 
        component="div" 
        id='dialog-description'
        className={contentTextClassName || ''}
        children={children}
      />
    </MuiDialogContent>
  );
}


export const DialogActions = (props) => {
  const { children, className } = props;

  return (
    <MuiDialogActions className={className || ''}>
      {children}
    </MuiDialogActions>
  );
}


export const DialogActionsEnhanced = (props) => {
  const { children, className } = props;
  const classes = useStyles();

  return (
    <MuiDialogActions className={`${classes.enhancedActions} ${className || ''}`}>
      {children}
    </MuiDialogActions>
  );
}


export default function Dialog(props) {
  const {
    title,
    titleIcon,
    titleActions,
    open,
    onClose,
    onOk,
    showOk = true,
    autoFocusOkButton = true,
    okLabel = 'OK',
    okDisabled = false,
    okVariant = 'text',
    cancelLabel = 'Cancel',
    showCancel = true,
    extraActions,
    contentProps,
    actionsProps,
    contentWrap,
    children,
    ...rest
  } = props;


  const onClickOk = () => {
    if (onOk) {
      onOk();
    }
  }


  const baseContent = (
    <>
      <DialogTitle icon={titleIcon} title={title} actions={titleActions} />
      <DialogContent {...contentProps}>{children}</DialogContent>
      <DialogActions {...actionsProps}>
        {extraActions}
        {showCancel &&
        <Button 
          onClick={() => onClose()} 
          data-testid='dialog-cancel-button'
          children={cancelLabel}
        />}
        {showOk &&
        <Button 
          onClick={onClickOk} 
          color="primary" 
          variant={okVariant}
          autoFocus={autoFocusOkButton} 
          data-testid='dialog-ok-button'
          disabled={okDisabled}
          children={<strong>{okLabel}</strong>}
        />}
      </DialogActions>
    </>
  );


  const content = contentWrap
    ? React.cloneElement(contentWrap, { children: baseContent })
    : baseContent;


  return (
    <>
      <MuiDialog
        open={open}
        onClose={() => onClose()}
        aria-labelledby='dialog-title'
        aria-describedby='dialog-description'
        {...rest}
        children={content}
      />
    </>
  );
}
